<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :min-w="3"
        :min-h="minHeight"
        :resized="handleResized"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title-prepend>
            <go-back-icon-button @click="goBack" v-if="showTable" />
            <b-dropdown class="arrivals-dropdown" variant="link" no-caret toggle-class="p-0 mr-2">
                <template #button-content>
                    <font-awesome-icon class="text-secondary" :icon="['fas', 'ellipsis-vertical']" />
                </template>
                <b-dropdown-item :key="filter.filter" v-for="filter in filters" @click="setFilter(filter.filter)">
                    {{ filter.title }}
                </b-dropdown-item>
            </b-dropdown>
        </template>
        <template v-slot:card-title>
            {{ title }}
        </template>

        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <div v-if="!showTable" class="h-100 position-relative">
                <vue-apex-charts
                    v-show="isDisplayGraph"
                    id="arrivals-pie-chart"
                    class="arrivals-pie-chart"
                    ref="arrivalsChart"
                    type="pie"
                    height="330"
                    :series="series"
                    :options="options"
                />
                <arrivals-card-grid
                    v-show="!isDisplayGraph"
                    :getDataEndpoint="getDataEndpoint"
                    :getDataParams="getDataParams"
                    :cardGridKey="cardGridKey"
                    :limit="limit"
                    :tabWidget="tabWidget"
                />
            </div>
            <div v-else class="h-100">
                <arrivals-card-grid
                    v-if="isArrivalsTable"
                    :getDataEndpoint="getDataEndpoint"
                    :getDataParams="getDataParams"
                    :cardGridKey="cardGridKey"
                    :limit="limit"
                    :tabWidget="tabWidget"
                />
                <card-grid
                    v-if="isAvailablePropertiesTable"
                    ref="grid"
                    :button-toolbar-class="[]"
                    :is-search="false"
                    :pagination="true"
                    :key="cardGridKey"
                    :rowData="rowData"
                    :columnDefs="propertyColumnDefs"
                    :pagination-page-size="limit"
                    :is-auto-width="true"
                    :font-size="tabWidget.settings.table_font_size"
                >
                </card-grid>
            </div>
        </template>

        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
                :withBlockedReservations="true"
                :withTableFontSize="true"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import CardGrid from '../../../generic/card-grid/card-grid.vue';
import CardGridItem from '../common/card-grid-item.vue';
import ArrivalsCardGrid from '../common/arrivals-card-grid.vue';
import {
    DATE_PERIOD_TODAY,
    DATE_PERIOD_TOMORROW,
    DATE_PERIOD_THIS_WEEK,
    DATE_PERIOD_NEXT_WEEK,
    DATE_PERIOD_FUTURE,
    DATE_PERIOD_MAPPING,
} from '../../../../constants';
import {
    DASHBOARD_ARRIVALS_GRID_ENDPOINT,
    DASHBOARD_AVAILABLE_PROPERTY_ENDPOINT,
    DASHBOARD_AVAILABLE_PROPERTY_COUNT_ENDPOINT,
    DASHBOARD_ARRIVALS_COUNT_ENDPOINT,
} from '../../../../endpoints';
import { getWidgetGridRowsNumber } from '../helpers';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import VueApexCharts from 'vue3-apexcharts';
import { isMediumScreen } from '../../../../utils/screens';
import GoBackIconButton from '../../../generic/go-back-icon-button/go-back-icon-button.vue';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

const AVAILABLE_PROPERTY_INDEX = 0;
const REMAINING_ARRIVALS_INDEX = 1;
const goBackButtonHeight = 43;

export default {
    name: 'ArrivalsStatisticsPieChartCard',
    mixins: [cardGridItemBaseMixin],
    components: {
        BusyModal,
        VueApexCharts,
        CardGrid,
        CardGridItem,
        ResponsiveButton,
        TabWidgetSettingsModalForm,
        GoBackIconButton,
        ArrivalsCardGrid,
    },
    data() {
        return {
            busy: null,
            currentFilter: DATE_PERIOD_TODAY,
            showTable: null,
            series: [],
            hasData: false,
            selectedChartPart: null,
            rowData: [],
            limit: getWidgetGridRowsNumber({
                tabWidget: this.tabWidget,
            }),
        };
    },
    mounted() {
        this.fetchChartData();
    },
    computed: {
        propertyColumnDefs() {
            return [
                {
                    field: 'name',
                    headerName: 'name',
                    sort: 'asc',
                    minWidth: this.isMobile ? 200 : null,
                    autoWidthExclude: true,
                },
                {
                    field: 'property_type',
                    headerName: 'Type',
                    // maxWidth: 140,
                },
                {
                    field: 'n_baths',
                    headerName: 'Baths',
                    // maxWidth: 100,
                },
                {
                    field: 'n_bedrooms',
                    headerName: 'Bedrooms',
                    // maxWidth: 120,
                },
                {
                    field: 'n_beds',
                    headerName: 'Beds',
                    // maxWidth: 100,
                },
                {
                    field: 'n_sleeps',
                    headerName: 'Sleeps',
                    // maxWidth: 100,
                },
                {
                    field: 'n_units',
                    headerName: 'Units',
                    // maxWidth: 100,
                },
            ];
        },
        filters() {
            return [
                {
                    title: DATE_PERIOD_MAPPING[DATE_PERIOD_TODAY],
                    filter: DATE_PERIOD_TODAY,
                },
                {
                    title: DATE_PERIOD_MAPPING[DATE_PERIOD_TOMORROW],
                    filter: DATE_PERIOD_TOMORROW,
                },
                {
                    title: DATE_PERIOD_MAPPING[DATE_PERIOD_THIS_WEEK],
                    filter: DATE_PERIOD_THIS_WEEK,
                },
                {
                    title: DATE_PERIOD_MAPPING[DATE_PERIOD_NEXT_WEEK],
                    filter: DATE_PERIOD_NEXT_WEEK,
                },
                {
                    title: DATE_PERIOD_MAPPING[DATE_PERIOD_FUTURE],
                    filter: DATE_PERIOD_FUTURE,
                },
            ];
        },
        options() {
            return {
                chart: {
                    events: {
                        dataPointSelection: (event, chartContext, config) => {
                            this.selectedChartPart = config.dataPointIndex;
                            if (this.isAvailablePropertiesTable) {
                                this.fetchPropertiesData();
                            }
                            this.$nextTick(() => {
                                this.showTable = true;
                                this.handleResized();
                            });
                        },
                        dataPointMouseEnter: function (event) {
                            const path = event.path || (event.composedPath && event.composedPath());
                            if (path) {
                                path[0].style.cursor = 'pointer';
                            }
                        },
                    },
                    width: '100%',
                },
                colors: ['#008FFB', '#00E396'],
                legend: {
                    show: true,
                    position: 'right',
                    fontSize: '16px',
                    itemMargin: {
                        vertical: 10,
                    },
                },
                responsive: [
                    {
                        breakpoint: 480,
                        options: {
                            chart: {
                                width: '100%',
                            },
                            legend: {
                                position: 'bottom',
                                fontSize: '12px',
                                itemMargin: {
                                    vertical: 5,
                                    horizontal: 5,
                                },
                            },
                        },
                    },
                ],
                dataLabels: {
                    enabled: true,
                    formatter: (val, { seriesIndex, w }) => {
                        return this.hasData ? this.series[seriesIndex] : 0;
                    },
                },
                tooltip: {
                    enabled: this.hasData,
                },
                labels: ['Available Properties', this.title],
            };
        },
        title() {
            const currentTitle = this.filters.find((item) => item.filter === this.currentFilter);
            return (this.tabWidget.settings.name || this.defaultName) + ' ' + currentTitle.title;
        },
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                    display_blocked_reservations: this.tabWidget.settings.display_blocked_reservations,
                    table_font_size: this.tabWidget.settings.table_font_size || this.defaultWidgetFontSize,
                },
            };
        },
        isArrivalsTable() {
            return this.selectedChartPart === REMAINING_ARRIVALS_INDEX;
        },
        isAvailablePropertiesTable() {
            return this.selectedChartPart === AVAILABLE_PROPERTY_INDEX;
        },
        isDisplayGraph() {
            return this.currentFilter === DATE_PERIOD_TODAY || this.currentFilter === DATE_PERIOD_TOMORROW;
        },
        isMobile() {
            return isMediumScreen();
        },
        getDataParams() {
            return {
                period: this.currentFilter,
            };
        },
        getDataEndpoint() {
            return DASHBOARD_ARRIVALS_GRID_ENDPOINT();
        },
    },
    methods: {
        goBack() {
            this.showTable = false;
        },
        async fetchChartData() {
            try {
                this.busy = true;

                const [responseProperty, responseArrivals] = await Promise.all([
                    API.get(DASHBOARD_AVAILABLE_PROPERTY_COUNT_ENDPOINT(), {
                        params: {
                            period: this.currentFilter,
                        },
                    }),
                    API.get(DASHBOARD_ARRIVALS_COUNT_ENDPOINT(), {
                        params: {
                            period: this.currentFilter,
                            with_blocked: this.tabWidget.settings.display_blocked_reservations,
                        },
                    }),
                ]);
                if ([responseProperty.data, responseArrivals.data].reduce((a, b) => a + b, 0) > 0) {
                    this.series = [responseProperty.data, responseArrivals.data];
                    this.hasData = true;
                } else {
                    this.series = [1,1];
                    this.hasData = false;
                }
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        async fetchPropertiesData() {
            try {
                const { data } = await API.get(DASHBOARD_AVAILABLE_PROPERTY_ENDPOINT(), {
                    params: {
                        period: this.currentFilter,
                        limit: 20,
                        offset: 0,
                    },
                });
                this.rowData = data.data || data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            }
        },
        handleResized() {
            this.limit = getWidgetGridRowsNumber({
                tabWidget: this.tabWidget,
            });
            this.cardGridKey += 1;
        },
        setFilter(filter) {
            this.currentFilter = filter;
            this.showTable = false;
            if (this.currentFilter === DATE_PERIOD_TODAY || this.currentFilter === DATE_PERIOD_TOMORROW) {
                this.fetchChartData();
            }
            this.handleResized();
        },
    },
};
</script>

<style lang="scss">
.arrivals-pie-chart {
    .apexcharts-datalabels {
        opacity: 0.9 !important;
        &.legend-mouseover-inactive {
            opacity: 0.9 !important;
        }
    }
}
</style>
<style scoped lang="scss">
.arrivals-dropdown {
    padding-bottom: 2px;
}
</style>
